import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HeatmapModule from "highcharts/modules/heatmap";
import HighchartsReact from "highcharts-react-official";
import { getHeatmapOptions } from "@mappers/model/HeatmapMapper";
import { prepareBackTestHeatmapData } from "./utils";
import { BackTestResult } from "./types";
import { FetchParam } from "@store/reducers/userDefinedFlags.reducer";

HeatmapModule(Highcharts);

const CELL_SIZE = 16;
const CELL_GAP = 2;

interface BackTestHeatmapProps {
    result: BackTestResult;
    players: any[];
    fetchParams?: Record<string, FetchParam>;
    flagColor?: string;
}

const BackTestHeatmap: React.FC<BackTestHeatmapProps> = ({ result, players, fetchParams, flagColor }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width ?? 0;
            if (w > 0) setContainerWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const options = useMemo(() => {
        const data = prepareBackTestHeatmapData(result, players, fetchParams, flagColor);
        const baseOptions = getHeatmapOptions({
            ...data,
            cellSize: CELL_SIZE,
            cellGap: CELL_GAP,
            backgroundColor: "transparent",
        });

        const naturalWidth = (baseOptions as any).chart.width as number;
        const originalMarginRight = (baseOptions as any).chart.marginRight as number;

        // When the container is wider than the compact chart, extend the SVG rightward by
        // growing marginRight instead of the plot area — cells stay compact, the hamburger
        // menu reaches the container's right edge, and the transparent gap is invisible.
        if (containerWidth > naturalWidth) {
            return {
                ...baseOptions,
                chart: {
                    ...(baseOptions as any).chart,
                    width: containerWidth,
                    marginRight: containerWidth - naturalWidth + originalMarginRight,
                },
            };
        }

        return baseOptions;
    }, [result, players, fetchParams, flagColor, containerWidth]);

    const chartWidth = (options as any).chart?.width as number | undefined;

    return (
        <div ref={containerRef} className="udf-heatmap-container" style={{ marginTop: 16 }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { minWidth: chartWidth } }}
            />
        </div>
    );
};

export default BackTestHeatmap;
